/**
 * שווי נקי — "כמה אנחנו שווים, ממה זה מורכב, ואיך זה השתנה בשנה" (spec §4.5, §6.4).
 * 13 points (12 month-ends + today); points reconstructed or carried forward are marked
 * "אומדן" and the caption says from when the history is real (ARCHITECTURE §8).
 */

import type { NetWorthAccount, NetWorthResponse } from '../../../shared-types.ts';
import { fmtDate, fmtMoney, fmtPct, monthName, monthShort } from '../format.ts';
import { he, providerName } from '../i18n/he.ts';
import { Icon } from '../components/Icon.tsx';
import { Chip, EmptyState, Ltr, Money, ProviderLogo, Section } from '../components/ui.tsx';
import { CategoryIcon as KitCategoryIcon } from '../../../../shared/ui/src/index.tsx';
import { LineChart } from '../charts/LineChart.tsx';
import { useApp } from '../hooks/useApp.tsx';
import { useQuery } from '../hooks/useQuery.ts';
import { cacheKey } from '../hooks/cache.ts';
import { Link } from '../hooks/useRouter.tsx';
import { buildPath } from '../router.ts';

export function NetWorthScreen() {
  const { api } = useApp();
  const query = useQuery<NetWorthResponse>(cacheKey('networth'), () => api.netWorth(), { scopes: ['networth', 'summary'] });
  if (!query.data) {
    return (
      <div className="screen">
        <div className="sk" style={{ height: '260px' }} />
        <div className="sk" style={{ height: '320px' }} />
      </div>
    );
  }
  const data = query.data;
  const up = data.monthChangeAgorot >= 0;
  const yearUp = (data.yearChangeAgorot ?? 0) >= 0;
  const total = data.assetsAgorot + data.liabilitiesAgorot || 1;
  const assetsShare = (data.assetsAgorot / total) * 100;
  const closedLoans = data.liabilities.filter((row) => row.group === 'closed_loan');
  const openLiabilities = data.liabilities.filter((row) => row.group !== 'closed_loan');

  return (
    <div className="screen">
      <div className="g12">
        <Section span={5} variant="hero" titleId="h-nw" title={he.nw.title} sub={he.nw.sub}>
          <div className="stat">
            <span className="v huge">
              <Money agorot={data.netAgorot} />
            </span>
          </div>
          <div className="stack" style={{ gap: '4px' }}>
            <span className={`small ${up ? 'good' : 'critical'}`}>
              <Icon name={up ? 'upRight' : 'downLeft'} className="xs" /> {he.nw.thisMonth(fmtMoney(data.monthChangeAgorot, 0, true), fmtPct(data.monthChangePct))}
            </span>
            {data.yearChangeAgorot !== null ? (
              <span className={`small ${yearUp ? 'good' : 'critical'}`}>
                <Icon name={yearUp ? 'upRight' : 'downLeft'} className="xs" /> {he.nw.year(fmtMoney(data.yearChangeAgorot, 0, true), fmtPct(data.yearChangePct))}
              </span>
            ) : null}
          </div>
          <div className="stack" style={{ gap: '6px' }}>
            <div className="stackbar" role="img" aria-label={he.nw.assetsVsLiabilities(fmtMoney(data.assetsAgorot), fmtMoney(data.liabilitiesAgorot))}>
              <i style={{ width: `${assetsShare.toFixed(1)}%`, background: 'var(--series-1)' }} />
              <i style={{ width: `${(100 - assetsShare).toFixed(1)}%`, background: 'var(--series-2)' }} />
            </div>
            <div className="row between meta">
              <span>
                <i style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: 'var(--series-1)', verticalAlign: '-1px' }} /> {he.nw.assets}{' '}
                <span className="num">{fmtMoney(data.assetsAgorot)}</span>
              </span>
              <span>
                <i style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: 'var(--series-2)', verticalAlign: '-1px' }} /> {he.nw.liabilities}{' '}
                <span className="num">{fmtMoney(data.liabilitiesAgorot)}</span>
              </span>
            </div>
          </div>
        </Section>

        <Section span={7}>
          <LineChart
            question={he.nw.chartQ}
            period={`${he.nw.chartPeriod(monthName(data.history[0]?.month ?? ''), monthName(data.history[data.history.length - 1]?.month ?? ''))} · ${he.nw.historyFrom(fmtDate(data.historyFrom))}`}
            summary={`מ-${fmtMoney(data.history[0]?.netAgorot ?? 0)} ל-${fmtMoney(data.netAgorot)}`}
            step={1000000}
            points={data.history.map((point) => ({
              label: point.isCurrent ? he.nw.today : monthShort(point.month),
              value: point.netAgorot,
              aria: `${point.isCurrent ? he.nw.today : monthName(point.month)} ${fmtMoney(point.netAgorot)}`,
              tip: {
                title: point.isCurrent ? `${he.nw.today} · ${fmtDate(point.day)}` : monthName(point.month),
                rows: [
                  { value: fmtMoney(point.netAgorot), label: he.nw.tblNet },
                  { value: fmtMoney(point.assetsAgorot), label: he.nw.assets },
                  { value: fmtMoney(point.liabilitiesAgorot), label: he.nw.liabilities },
                  ...(point.estimated ? [{ label: he.nw.estimateNote, muted: true }] : []),
                ],
              },
            }))}
            table={{
              head: [he.cats.tblMonth, he.nw.tblNet, he.nw.assets, he.nw.liabilities],
              rows: data.history.map((point) => [
                point.isCurrent ? he.nw.today : monthName(point.month),
                fmtMoney(point.netAgorot),
                fmtMoney(point.assetsAgorot),
                fmtMoney(point.liabilitiesAgorot),
              ]),
            }}
          />
        </Section>

        <Section span={6} ariaLabel={he.nw.assets}>
          <details className="acc" open style={{ border: 0 }}>
            <summary className="acc-head">
              <h2 style={{ fontSize: '16px' }}>
                {he.nw.assets} · <span className="num">{fmtMoney(data.assetsAgorot)}</span>
              </h2>
              <Icon name="chevD" />
            </summary>
            <div className="hairlist">
              {data.assets.map((row) => (
                <NetWorthRow key={row.accountId ?? row.manualEntryId ?? row.group} row={row} />
              ))}
              {data.securities.status === 'not_connected' && !data.assets.some((row) => row.group === 'securities') ? (
                <div className="listrow" style={{ opacity: 0.75 }}>
                  <span className="main">
                    <span className="name">
                      {he.nw.securities} <Chip tone="muted" icon="link">{he.nw.notConnected}</Chip>
                    </span>
                    <span className="sub" style={{ whiteSpace: 'normal' }}>
                      {he.nw.securitiesNote}
                    </span>
                  </span>
                  <span className="amt">
                    <span className="num tab t3">—</span>
                  </span>
                </div>
              ) : null}
            </div>
          </details>
        </Section>

        <Section span={6} ariaLabel={he.nw.liabilities}>
          <details className="acc" open style={{ border: 0 }}>
            <summary className="acc-head">
              <h2 style={{ fontSize: '16px' }}>
                {he.nw.liabilities} · <span className="num">{fmtMoney(data.liabilitiesAgorot)}</span>
              </h2>
              <Icon name="chevD" />
            </summary>
            <div className="hairlist">
              {openLiabilities.length ? (
                openLiabilities.map((row) => <NetWorthRow key={row.accountId ?? row.manualEntryId ?? row.name} row={row} liability />)
              ) : (
                <EmptyState title={he.nw.liabilities} />
              )}
            </div>
            {closedLoans.length ? (
              <details className="acc" style={{ marginTop: '8px' }}>
                <summary>
                  <span>
                    {he.nw.closedLoans} · <span className="num">{closedLoans.length}</span>
                  </span>
                  <Icon name="chevD" />
                </summary>
                <div className="hairlist">
                  {closedLoans.map((row) => (
                    <NetWorthRow key={row.accountId ?? row.name} row={row} liability />
                  ))}
                </div>
              </details>
            ) : null}
          </details>
        </Section>
      </div>
    </div>
  );
}

function NetWorthRow({ row, liability }: { row: NetWorthAccount; liability?: boolean }) {
  const share = Math.round(row.share * 100);
  const change = row.monthChangeAgorot;
  const changeText =
    change === null ? he.nw.noHistory : change === 0 ? he.nw.noChange : he.nw.monthChange(fmtMoney(change, 0, true));
  const good = liability && change !== null && change < 0;
  const note = row.note;
  return (
    <div className="listrow" style={{ alignItems: 'flex-start', padding: '10px 0' }}>
      {row.providerId ? (
        <ProviderLogo providerId={row.providerId} title={providerName(row.providerId)} />
      ) : (
        <KitCategoryIcon glyph="piggy" hue="neutral" soft size={20} title={he.app.manual} />
      )}
      <span className="main">
        <span className="name">
          <Ltr>{`${row.name}${row.numberLast4 ? ` ***${row.numberLast4}` : ''}`}</Ltr>{' '}
          {row.dataAsOf ? (
            <span className="tag critical">
              <Icon name="alert" className="xs" />
              נכון ל-{fmtDate(row.dataAsOf.slice(0, 10))}
            </span>
          ) : null}{' '}
          <span className="meta num">{share}%</span>
        </span>
        <span className="share" aria-hidden="true" style={{ margin: '4px 0' }}>
          <i className={liability ? 's2' : ''} style={{ width: `${share}%` }} />
        </span>
        <span className="sub" style={{ whiteSpace: 'normal' }}>
          {row.interest?.ratePct != null ? he.nw.interest(String(row.interest.ratePct)) : ''}
          {note?.maturityDate ? ` · ${he.nw.renews(fmtDate(note.maturityDate))}` : ''}
          {note?.monthlyPaymentAgorot ? ` · ${he.nw.monthlyPayment(fmtMoney(note.monthlyPaymentAgorot))}` : ''}
          {note?.remainingPayments ? ` · ${he.nw.remainingPayments(note.remainingPayments)}` : ''}
          {note ? (
            <>
              {' '}
              <span className="meta">
                (<Link to={buildPath('settings', 'accounts')}>{he.nw.manualEdit}</Link>)
              </span>
            </>
          ) : null}
          {row.card ? (
            <>
              {row.card.finalAgorot
                ? he.nw.cardFinal(fmtMoney(row.card.finalAgorot), fmtDate(row.card.finalDueDate ?? ''))
                : he.nw.cardAccruedFrom(fmtDate(row.card.accruedFrom ?? ''))}
              {row.card.finalAgorot ? ` ${he.nw.cardAccrued(fmtMoney(row.card.accruedAgorot))}` : ''}
            </>
          ) : null}
        </span>
        <span className={`sub ${good ? 'good' : ''}`} style={{ whiteSpace: 'normal' }}>
          {changeText}
          {good ? ` · ${he.nw.lessDebt}` : ''}
        </span>
      </span>
      <span className="amt">
        <span className="num tab">{fmtMoney(row.balanceAgorot)}</span>
      </span>
    </div>
  );
}
